<template>
  <main class="efetivadas-page">
    <header class="intro">
      <div>
        <p class="eyebrow">Visão consolidada</p>
        <p class="description">
          Acordos concluídos no Negocie Já atual e no sistema legado, reunidos em uma única consulta.
        </p>
      </div>
      <div class="summary" aria-label="Resumo dos resultados">
        <div class="summary-item">
          <strong>{{ rows.length }}</strong>
          <span>Resultados</span>
        </div>
        <div class="summary-item current">
          <strong>{{ sourceCounts.atual }}</strong>
          <span>Negocie Já atual</span>
        </div>
        <div class="summary-item legacy">
          <strong>{{ sourceCounts.legado }}</strong>
          <span>Legado</span>
        </div>
      </div>
    </header>

    <nav class="filter-card" aria-label="Filtros da listagem">
      <form @submit.prevent="loadEfetivadas">
        <div class="filter-heading">
          <div>
            <strong>Filtrar negociações</strong>
            <span>Pesquise pelo aluno, responsável financeiro ou período do acordo.</span>
          </div>
          <button
            v-if="hasFilters"
            type="button"
            class="clear-filter"
            :disabled="loading"
            @click="clear"
          >
            Limpar filtros
          </button>
        </div>

        <div class="filter-grid">
          <div class="name-filter">
            <input-control
              label="Aluno ou responsável financeiro"
              placeholder="Digite um nome"
              v-model="filters.nome"
            />
          </div>
          <input-control type="date" label="Data inicial" v-model="filters.inicio" />
          <input-control type="date" label="Data final" v-model="filters.fim" />
          <button type="submit" class="btn btn-primary submit-filter" :disabled="loading">
            {{ loading ? 'Buscando...' : 'Buscar' }}
          </button>
        </div>
      </form>
    </nav>

    <section class="results-card">
      <div class="results-toolbar">
        <div>
          <strong>Negociações efetivadas</strong>
          <span>{{ visibleRows.length }} {{ visibleRows.length === 1 ? 'registro exibido' : 'registros exibidos' }}</span>
        </div>
        <div class="source-tabs" aria-label="Filtrar por origem">
          <button
            v-for="option in sourceOptions"
            :key="option.value"
            type="button"
            :class="{ active: sourceFilter === option.value }"
            @click="sourceFilter = option.value"
          >
            {{ option.label }}
            <span>{{ option.count }}</span>
          </button>
        </div>
      </div>

      <FormSpinner v-if="loading" full />

      <div v-else-if="visibleRows.length" class="table-wrap">
        <table class="negotiations-table">
          <thead>
            <tr>
              <th>Negociação</th>
              <th>Aluno</th>
              <th>Responsável financeiro</th>
              <th>Campanha / Pagamento</th>
              <th class="money">Valor</th>
              <th>Efetivada em</th>
              <th class="actions">Termo</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in visibleRows" :key="`${row.origem}-${row.idNegociacaoEfetivada}`">
              <td>
                <strong>#{{ row.idProposta || row.idNegociacaoEfetivada }}</strong>
                <span class="source-badge" :class="row.origem">
                  {{ row.origem === 'atual' ? 'Atual' : 'Legado' }}
                </span>
              </td>
              <td>{{ row.nomeAluno || 'Não informado' }}</td>
              <td>{{ row.nomeResponsavel || 'Não informado' }}</td>
              <td>
                <strong class="cell-title">{{ row.nomeCampanha || 'Sem campanha' }}</strong>
                <span class="cell-subtitle">{{ row.nomeFormaPagamento || 'Forma não informada' }}</span>
              </td>
              <td class="money">{{ formatMoney(row.valor) }}</td>
              <td>{{ formatDate(row.dataAcordo) }}</td>
              <td class="actions">
                <button
                  type="button"
                  class="download-button"
                  :disabled="!hasTerm(row) || downloadingKey === rowKey(row)"
                  :title="hasTerm(row) ? 'Baixar termo de acordo' : 'Termo não disponível'"
                  @click="downloadTermo(row)"
                >
                  <fa :icon="['fa', 'file-arrow-down']" />
                  <span>{{ downloadingKey === rowKey(row) ? 'Baixando' : 'Baixar' }}</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="empty-state">
        <div class="empty-icon"><fa :icon="['fa', 'file-circle-exclamation']" /></div>
        <strong>Nenhuma negociação encontrada</strong>
        <span>Ajuste os filtros ou selecione outra origem para ampliar a busca.</span>
      </div>

      <p v-if="!loading && (sourceCounts.atual === 200 || sourceCounts.legado === 200)" class="result-limit">
        Exibindo até 200 acordos por origem. Refine o período ou o nome para localizar registros anteriores.
      </p>
    </section>
  </main>
</template>

<script>
import RenegociacoesService from '@/services/renegociacoes.service'
import docusignService from '@/services/docusign.service'
import { formatDate, formatMoney } from '@/services/utils.service.js'
import FormSpinner from '@/components/utilities/FormSpinner.vue'

export default {
  name: 'EfetivadasLista',
  components: { FormSpinner },
  data() {
    return {
      loading: false,
      downloadingKey: null,
      rows: [],
      sourceFilter: 'todas',
      filters: {
        nome: '',
        inicio: '',
        fim: ''
      }
    }
  },
  computed: {
    hasFilters() {
      return Object.values(this.filters).some(value => String(value || '').trim())
    },
    sourceCounts() {
      return this.rows.reduce(
        (counts, row) => {
          if (row.origem === 'atual') counts.atual += 1
          if (row.origem === 'legado') counts.legado += 1
          return counts
        },
        { atual: 0, legado: 0 }
      )
    },
    sourceOptions() {
      return [
        { value: 'todas', label: 'Todas', count: this.rows.length },
        { value: 'atual', label: 'Atual', count: this.sourceCounts.atual },
        { value: 'legado', label: 'Legado', count: this.sourceCounts.legado }
      ]
    },
    visibleRows() {
      if (this.sourceFilter === 'todas') return this.rows
      return this.rows.filter(row => row.origem === this.sourceFilter)
    }
  },
  methods: {
    formatMoney,
    formatDate,
    rowKey(row) {
      return `${row.origem}-${row.idNegociacaoEfetivada}`
    },
    hasTerm(row) {
      return row.origem === 'legado'
        ? Boolean(row.urlTermoAcordo)
        : Boolean(row.envelopeId || row.urlTermoAcordo)
    },
    async loadEfetivadas() {
      if (this.filters.inicio && this.filters.fim && this.filters.inicio > this.filters.fim) {
        window.toaster.emit('open', {
          style: 'error',
          message: 'A data inicial não pode ser maior que a final',
          floater: true
        })
        return
      }

      this.loading = true
      try {
        const params = Object.fromEntries(
          Object.entries(this.filters)
            .map(([key, value]) => [key, String(value || '').trim()])
            .filter(([, value]) => value)
        )
        const { data } = await RenegociacoesService.getEfetivadas(params)
        this.rows = Array.isArray(data?.data) ? data.data : []
      } catch (error) {
        console.error('Falha ao carregar negociações efetivadas', error)
        this.rows = []
        window.toaster.emit('open', {
          style: 'error',
          message: 'Não foi possível carregar as negociações efetivadas',
          floater: true
        })
      } finally {
        this.loading = false
      }
    },
    async clear() {
      this.filters.nome = ''
      this.filters.inicio = ''
      this.filters.fim = ''
      this.sourceFilter = 'todas'
      await this.loadEfetivadas()
    },
    saveBlob(blob, filename) {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },
    decodeSignedTerm(payload) {
      if (payload instanceof ArrayBuffer || ArrayBuffer.isView(payload)) {
        return new Blob([payload], { type: 'application/pdf' })
      }

      const raw = typeof payload === 'string' ? payload : payload?.pdf
      if (!raw) throw new Error('Retorno vazio do documento assinado')

      const clean = String(raw)
        .replace(/^data:application\/pdf;base64,?/i, '')
        .replace(/-/g, '+')
        .replace(/_/g, '/')
      const padded = clean + '='.repeat((4 - (clean.length % 4)) % 4)
      const decoded = window.atob(padded)
      const bytes = Uint8Array.from(decoded, character => character.charCodeAt(0))

      return new Blob([bytes], { type: 'application/pdf' })
    },
    async downloadTermo(row) {
      this.downloadingKey = this.rowKey(row)
      try {
        if (row.origem === 'atual') {
          if (row.envelopeId) {
            const response = await docusignService.getDocumentByEnvolopeId(row.envelopeId)
            const blob = this.decodeSignedTerm(response.data)
            this.saveBlob(blob, `termo-acordo-${row.idProposta}.pdf`)
          } else {
            window.open(row.urlTermoAcordo, '_blank', 'noopener')
          }
          return
        }

        const response = await RenegociacoesService.downloadTermo(row.idRenegociacaoEfetivada)
        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || 'application/pdf'
        })
        const disposition = response.headers['content-disposition'] || ''
        const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1]
        this.saveBlob(blob, filename || `termo-acordo-${row.idNegociacaoEfetivada}.pdf`)
      } catch (error) {
        console.error('Falha ao baixar termo de acordo', error)
        window.toaster.emit('open', {
          style: 'error',
          message: 'Não foi possível baixar o termo de acordo',
          floater: true
        })
      } finally {
        this.downloadingKey = null
      }
    }
  },
  mounted() {
    this.loadEfetivadas()
  }
}
</script>

<style lang="scss" scoped>
.efetivadas-page {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  background: #f6f7f9;
}

.intro {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
}

.eyebrow {
  margin: 0 0 4px;
  color: $color-primary;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.description {
  max-width: 640px;
  margin: 0;
  color: #687080;
  font-size: 14px;
  line-height: 1.5;
}

.summary {
  display: flex;
  gap: 8px;
}

.summary-item {
  min-width: 108px;
  padding: 10px 14px;
  border: 1px solid #e3e6eb;
  border-radius: 10px;
  background: #fff;

  strong,
  span {
    display: block;
  }

  strong {
    color: #252a34;
    font-size: 20px;
    line-height: 1.15;
  }

  span {
    margin-top: 2px;
    color: #7a8291;
    font-size: 10px;
    white-space: nowrap;
  }

  &.current {
    border-top: 3px solid #2470dc;
  }

  &.legacy {
    border-top: 3px solid #89919f;
  }
}

.filter-card,
.results-card {
  border: 1px solid #e1e4e9;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 14px rgba(34, 40, 52, 0.04);
}

.filter-card {
  padding: 18px 20px 20px;
}

.filter-heading,
.results-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  strong,
  span {
    display: block;
  }

  strong {
    color: #2c313b;
    font-size: 14px;
  }

  span {
    margin-top: 3px;
    color: #838b99;
    font-size: 11px;
  }
}

.clear-filter {
  border: 0;
  background: transparent;
  color: $color-primary;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
}

.filter-grid {
  display: grid;
  grid-template-columns: minmax(280px, 2fr) minmax(160px, 0.7fr) minmax(160px, 0.7fr) auto;
  align-items: end;
  gap: 14px;
  margin-top: 16px;

  > * {
    min-width: 0;
    margin: 0 !important;
  }
}

.submit-filter {
  height: 40px;
  margin: 0 !important;
  padding-right: 24px;
  padding-left: 24px;
}

.results-card {
  overflow: hidden;
}

.results-toolbar {
  padding: 16px 20px;
  border-bottom: 1px solid #edf0f3;
}

.source-tabs {
  display: flex;
  gap: 4px;
  padding: 3px;
  border-radius: 8px;
  background: #f1f3f6;

  button {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #626b7a;
    cursor: pointer;
    font-size: 11px;
    font-weight: 600;

    span {
      min-width: 20px;
      margin: 0;
      padding: 1px 5px;
      border-radius: 10px;
      background: rgba(100, 108, 122, 0.12);
      color: inherit;
      text-align: center;
    }

    &.active {
      background: #fff;
      color: #242a34;
      box-shadow: 0 1px 4px rgba(35, 42, 53, 0.1);
    }
  }
}

.table-wrap {
  width: 100%;
  overflow-x: auto;
}

.negotiations-table {
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 13px 16px;
    border-bottom: 1px solid #edf0f3;
    text-align: left;
    vertical-align: middle;
  }

  th {
    background: #fafbfc;
    color: #78808e;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    white-space: nowrap;
  }

  td {
    color: #4b5260;
    font-size: 12px;
  }

  tbody tr:hover {
    background: #fcfcfd;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }

  .money {
    text-align: right;
    white-space: nowrap;
  }

  .actions {
    text-align: center;
  }
}

.source-badge {
  display: inline-block;
  margin-top: 5px;
  padding: 3px 7px;
  border-radius: 10px;
  font-size: 9px;
  font-weight: 700;

  &.atual {
    background: #e8f1ff;
    color: #1d63c4;
  }

  &.legado {
    background: #eef0f2;
    color: #646d79;
  }
}

.cell-title,
.cell-subtitle {
  display: block;
}

.cell-title {
  color: #3e4551;
  font-size: 12px;
}

.cell-subtitle {
  margin-top: 3px;
  color: #858d9a;
  font-size: 10px;
}

.download-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 9px;
  border: 1px solid #dfe3e8;
  border-radius: 7px;
  background: #fff;
  color: $color-primary;
  cursor: pointer;
  font-size: 10px;
  font-weight: 600;

  &:hover:not(:disabled) {
    border-color: $color-primary;
    background: #fff8f8;
  }

  &:disabled {
    color: #a8aeb8;
    cursor: not-allowed;
    opacity: 0.65;
  }
}

.empty-state {
  display: flex;
  min-height: 260px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 40px;
  text-align: center;

  strong {
    margin-top: 14px;
    color: #363c47;
    font-size: 15px;
  }

  > span {
    margin-top: 5px;
    color: #858d9a;
    font-size: 12px;
  }
}

.empty-icon {
  display: flex;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #f1f3f5;
  color: #9aa1ad;
  font-size: 18px;
}

.result-limit {
  margin: 0;
  padding: 11px 20px;
  border-top: 1px solid #edf0f3;
  background: #fafbfc;
  color: #7d8593;
  font-size: 10px;
  text-align: center;
}

@media (max-width: 1000px) {
  .intro {
    align-items: flex-start;
    flex-direction: column;
  }

  .filter-grid {
    grid-template-columns: 1fr 1fr;
  }

  .name-filter {
    grid-column: 1 / -1;
  }

  .submit-filter {
    width: 100%;
  }
}

@media (max-width: 680px) {
  .efetivadas-page {
    padding: 14px;
  }

  .summary {
    width: 100%;
    overflow-x: auto;
  }

  .summary-item {
    flex: 1 0 105px;
  }

  .filter-heading,
  .results-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .filter-grid {
    grid-template-columns: 1fr;
  }

  .name-filter {
    grid-column: auto;
  }

  .source-tabs {
    width: 100%;

    button {
      flex: 1;
      justify-content: center;
    }
  }
}
</style>
